import {
  createSceneRuntime,
  type BodyHandle,
  type FixedStepper,
  type SceneCommandEnvelope as UpstreamSceneCommandEnvelope,
  type SceneParticipant,
  type SceneRuntime,
  type ShapeIdentity,
} from 'box3d-web';
import { SharedDestructionWorld } from './sharedDestructionWorld';
import type { DestructionSceneParticipant, SceneCommandPolicy, SceneContactBatch } from './destructionSceneParticipant';

export type DestructionSceneCommandType = keyof DestructionSceneCommandPayload;

export type DestructionSceneCommandPayload = {
  fire: { tool: string; power: number; target: { x: number; y: number; z: number }; direction: { x: number; y: number; z: number } };
  'probe-impact': { x: number; y: number; z: number; speed?: number; direction?: { x: number; y: number; z: number } };
  'reset-structure': Record<string, never>;
  'place-charge': { x: number; y: number; z: number; power?: number };
  detonate: { x: number; y: number; z: number; power: number };
  'dispose-structure': Record<string, never>;
  'set-material': { material: string };
  'set-weapon-power': { power: number };
  'set-tool': { tool: string };
};

export interface DestructionSceneCommand<T extends string = DestructionSceneCommandType> {
  readonly sequence: number;
  readonly sceneEpoch: number;
  readonly fixedStepIndex: number;
  readonly targetStructureKey: string;
  readonly targetRevision: number;
  readonly type: T;
  readonly payload: SceneCommandPayload<T>;
}

type SceneCommandPayload<T extends string> = T extends keyof DestructionSceneCommandPayload
  ? DestructionSceneCommandPayload[T]
  : unknown;

export interface DestructionSceneCommandInput<T extends string = DestructionSceneCommandType> {
  readonly targetStructureKey: string;
  readonly type: T;
  readonly payload: SceneCommandPayload<T>;
}

export interface DestructionSceneTelemetry {
  readonly commands: { queued: number; coalesced: number; droppedCoalescible: number; rejectedCritical: number };
  readonly events: { retained: number; dropped: number; faults: number };
  readonly unsupportedCrossStructureContacts: number;
  readonly faulted: boolean;
}

export interface DestructionSceneEventEnvelope<TEvent = unknown> {
  readonly sceneEpoch: number;
  readonly fixedStep: number;
  readonly sequence: number;
  readonly structureKey: string;
  readonly structureRevision: number;
  readonly event: TEvent;
}

export interface DestructionSceneRuntimeOptions {
  readonly commandCapacity?: number;
  readonly eventCapacity?: number;
  readonly fixedDt?: number;
  readonly substeps?: number;
  readonly maxStepsPerFrame?: number;
}

interface SceneRuntimeSlot {
  commands: UpstreamSceneCommandEnvelope[];
  queued: number;
  coalesced: number;
  dropped: number;
  rejected: number;
  retained: number;
  droppedEvents: number;
  faults: number;
  unroutedContacts: number;
  epoch: number;
  fixedStepIndex: number;
  applyCommands: () => void;
  classifyContacts: (batch: SceneContactBatch) => void;
  enqueueCommand: (input: { targetParticipantKey: string; type: string; payload: unknown }) => UpstreamSceneCommandEnvelope;
  assertHealthy: () => void;
}

interface SceneRuntimePrototype {
  classifyContacts(batch: SceneContactBatch): void;
}

/** Generic scene authority for one shared world and any number of participants. */
export class DestructionSceneRuntime<TEvent = unknown> {
  readonly world: SharedDestructionWorld;
  readonly stepper: FixedStepper;
  private readonly inner: SceneRuntime<TEvent>;
  private readonly slot: SceneRuntimeSlot;
  private readonly participants = new Map<string, DestructionSceneParticipant<TEvent>>();
  private readonly revisionBias = new Map<string, number>();

  constructor(world: SharedDestructionWorld, options: DestructionSceneRuntimeOptions = {}) {
    this.world = world;
    if (options.fixedDt !== undefined && (!Number.isFinite(options.fixedDt) || options.fixedDt !== 1 / 60)) throw new RangeError('scene fixedDt must match scene timing');
    if (options.substeps !== undefined && (!Number.isInteger(options.substeps) || options.substeps !== 4)) throw new RangeError('scene substeps must match scene timing');
    const inner = createSceneRuntime<TEvent>({
      world: world.world,
      commandCapacity: options.commandCapacity,
      eventCapacity: options.eventCapacity,
      fixedDt: 1 / 60,
      substeps: 4,
      maxStepsPerFrame: options.maxStepsPerFrame,
      classifyContact: (begin) => this.isUnrouted(begin.shapeA, begin.shapeB) ? 'unrouted' : 'routed',
    });
    this.inner = inner;
    this.stepper = inner.stepper;
    this.slot = inner as unknown as SceneRuntimeSlot;
    const originalClassify = Object.getPrototypeOf(inner) as SceneRuntimePrototype;
    this.slot.applyCommands = () => { this.applyQueuedCommands(); };
    this.slot.classifyContacts = (batch) => {
      originalClassify.classifyContacts.call(inner, batch);
      for (const hit of batch.hits) {
        if (this.isUnrouted(hit.shapeA, hit.shapeB)) this.slot.unroutedContacts += 1;
      }
    };
  }

  attach(adapter: DestructionSceneParticipant<TEvent>): void {
    if (adapter.physicsWorld !== this.world.world) throw new Error('participant belongs to a different destruction world');
    if (this.participants.has(adapter.structureKey)) throw new Error(`structure ${adapter.structureKey} is already attached`);
    const adapted = this.adapt(adapter);
    this.participants.set(adapter.structureKey, adapter);
    try {
      this.inner.attach(adapted);
    } catch (error) {
      this.participants.delete(adapter.structureKey);
      throw error;
    }
  }

  detach(adapterOrKey: DestructionSceneParticipant<TEvent> | string): void {
    const key = typeof adapterOrKey === 'string' ? adapterOrKey : adapterOrKey.structureKey;
    this.inner.detach(key);
    this.participants.delete(key);
  }

  /** Remove queued input and retained output for a structure being replaced. */
  purgeStructure(structureKey: string): void {
    this.inner.purgeParticipant(structureKey);
  }

  enqueueCommand<T extends string>(input: DestructionSceneCommandInput<T>): DestructionSceneCommand<T> {
    this.slot.assertHealthy();
    const adapter = this.participants.get(input.targetStructureKey);
    if (adapter === undefined) throw new Error(`unknown structure ${input.targetStructureKey}`);
    const key = input.targetStructureKey;
    if (this.slot.commands.some((command) => command.targetParticipantKey === key && command.type === 'dispose-structure')) {
      if (this.commandPolicy(adapter, input.type) === 'critical') this.slot.rejected += 1;
      else this.slot.dropped += 1;
      throw new Error(`dispose for ${key} is already queued`);
    }
    if (input.type === 'reset-structure' && this.slot.commands.some((command) => command.targetParticipantKey === key && command.type === 'reset-structure')) {
      this.slot.rejected += 1;
      throw new Error(`reset for ${key} is already queued`);
    }
    if (input.type === 'reset-structure') {
      const retained: UpstreamSceneCommandEnvelope[] = [];
      for (const command of this.slot.commands) {
        if (command.targetParticipantKey !== key || command.type === 'reset-structure') {
          retained.push(command);
        } else if (this.commandPolicy(adapter, command.type) === 'critical') {
          this.slot.rejected += 1;
        } else {
          this.slot.dropped += 1;
        }
      }
      this.slot.commands = retained;
    }
    const pendingReset = input.type === 'reset-structure'
      || this.slot.commands.some((command) => command.targetParticipantKey === key && command.type === 'reset-structure');
    this.revisionBias.set(key, pendingReset ? 1 : 0);
    try {
      return this.toAppCommand(this.slot.enqueueCommand({
        targetParticipantKey: key,
        type: input.type,
        payload: input.payload,
      }));
    } finally {
      this.revisionBias.delete(key);
    }
  }

  advance(delta: number): number {
    return this.inner.advance(delta);
  }

  drainEvents(): readonly DestructionSceneEventEnvelope<TEvent>[] {
    return Object.freeze(this.inner.drainEvents().map((envelope) => Object.freeze({
      sceneEpoch: envelope.sceneEpoch,
      fixedStep: envelope.fixedStep,
      sequence: envelope.sequence,
      structureKey: envelope.participantKey,
      structureRevision: envelope.participantRevision,
      event: envelope.event,
    })));
  }

  reset(): void {
    this.inner.reset();
  }

  telemetry(): DestructionSceneTelemetry {
    const telemetry = this.inner.telemetry();
    return Object.freeze({
      commands: Object.freeze({
        queued: telemetry.queued,
        coalesced: telemetry.coalesced,
        droppedCoalescible: telemetry.dropped,
        rejectedCritical: telemetry.rejected,
      }),
      events: Object.freeze({
        retained: telemetry.retained,
        dropped: telemetry.droppedEvents,
        faults: telemetry.faults,
      }),
      unsupportedCrossStructureContacts: telemetry.unroutedContacts,
      faulted: telemetry.faulted,
    });
  }

  dispose(): void {
    this.inner.dispose();
    this.participants.clear();
  }

  private adapt(adapter: DestructionSceneParticipant<TEvent>): SceneParticipant<TEvent> {
    const outer = this;
    const key = adapter.structureKey;
    return {
      get participantKey() { return key; },
      get revision() { return adapter.revision + (outer.revisionBias.get(key) ?? 0); },
      get physicsWorld() { return adapter.physicsWorld; },
      get fixedDt() { return adapter.fixedDt; },
      get substeps() { return adapter.substeps; },
      dispose() { adapter.dispose(); },
      attachSceneOwner() { adapter.attachSceneOwner(outer); },
      detachSceneOwner() { adapter.detachSceneOwner(outer); },
      prepareSceneCommand: adapter.prepareSceneCommand?.bind(adapter),
      sceneCommandPolicy: adapter.sceneCommandPolicy?.bind(adapter),
      applySceneCommand(_owner, command) { adapter.applySceneCommand(outer, outer.toAppCommand(command)); },
      sceneFixedStep(_owner, batch) { adapter.sceneFixedStep(outer, batch); },
      finishSceneFrame(_owner, delta, stepped) { adapter.finishSceneFrame(outer, delta, stepped); },
      drainSceneEvents(_owner) { return adapter.drainSceneEvents(outer); },
      sceneReset(_owner) { adapter.sceneReset(outer); },
      sceneResetClock(_owner) { adapter.sceneResetClock(outer); },
      ownsBody: adapter.ownsProjectileBody.bind(adapter),
      resolveShape: adapter.resolveShape?.bind(adapter),
    };
  }

  private applyQueuedCommands(): void {
    const due = this.slot.commands
      .filter((command) => command.sceneEpoch === this.slot.epoch && command.fixedStepIndex <= this.slot.fixedStepIndex)
      .sort((left, right) => left.fixedStepIndex - right.fixedStepIndex || left.sequence - right.sequence);
    this.slot.commands = this.slot.commands.filter((command) => !due.includes(command));
    for (const command of due) {
      const adapter = this.participants.get(command.targetParticipantKey);
      if (!adapter) {
        if (this.commandPolicy(undefined, command.type) === 'critical') this.slot.rejected += 1;
        else this.slot.dropped += 1;
        throw new Error(`command target ${command.targetParticipantKey} is no longer attached`);
      }
      if (command.type !== 'reset-structure' && command.targetRevision !== adapter.revision) {
        if (this.commandPolicy(adapter, command.type) === 'critical') this.slot.rejected += 1;
        else this.slot.dropped += 1;
        throw new Error(`stale command revision for ${command.targetParticipantKey}`);
      }
      if (command.type === 'reset-structure') {
        adapter.sceneReset(this);
        continue;
      }
      if (command.type === 'dispose-structure') {
        this.detach(adapter);
        adapter.dispose();
        continue;
      }
      adapter.applySceneCommand(this, this.toAppCommand(command));
    }
  }

  private isUnrouted(shapeA: ShapeIdentity, shapeB: ShapeIdentity): boolean {
    const left = this.world.registry.resolveShape(shapeA);
    const right = this.world.registry.resolveShape(shapeB);
    if (!left || !right) return false;
    const leftAddress = left.bodyRegistration.address;
    const rightAddress = right.bodyRegistration.address;
    const leftProjectile = leftAddress.scope === 'world' && leftAddress.actor === 'projectile';
    const rightProjectile = rightAddress.scope === 'world' && rightAddress.actor === 'projectile';
    const leftStructure = leftAddress.scope === 'structure' ? leftAddress.structureId.key : undefined;
    const rightStructure = rightAddress.scope === 'structure' ? rightAddress.structureId.key : undefined;
    const ownerFor = (body: BodyHandle) => [...this.participants.values()].find((participant) => participant.ownsProjectileBody(body));
    const owner = leftProjectile ? ownerFor(left.body) : rightProjectile ? ownerFor(right.body) : undefined;
    const contacted = leftProjectile ? rightStructure : rightProjectile ? leftStructure : undefined;
    return Boolean(owner && contacted && owner.structureKey !== contacted);
  }

  private commandPolicy(participant: DestructionSceneParticipant<TEvent> | undefined, type: string): SceneCommandPolicy {
    return participant?.sceneCommandPolicy?.(type) ?? 'critical';
  }

  private toAppCommand<T extends string>(command: UpstreamSceneCommandEnvelope): DestructionSceneCommand<T> {
    return Object.freeze({
      sequence: command.sequence,
      sceneEpoch: command.sceneEpoch,
      fixedStepIndex: command.fixedStepIndex,
      targetStructureKey: command.targetParticipantKey,
      targetRevision: command.targetRevision,
      type: command.type as T,
      payload: command.payload as SceneCommandPayload<T>,
    });
  }
}
